(() => {
  'use strict';

  if (window.__mellerStickyAtcReady) return;
  window.__mellerStickyAtcReady = true;

  const BLOCK_SELECTOR = '[data-meller-buy-buttons]';
  const MOBILE_QUERY = '(max-width: 749px)';
  const registry = window.__mellerStickyATCRegistry || (window.__mellerStickyATCRegistry = new WeakMap());
  const mediaQuery = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
  const bodyObserverRegistry = window.__mellerStickyATCBodyObserverRegistry || (window.__mellerStickyATCBodyObserverRegistry = {
    observer: null,
    callbacks: new Set()
  });

  const isMobile = () => {
    return mediaQuery ? mediaQuery.matches : window.innerWidth < 750;
  };

  const isMobileMenuOpen = () => {
    return document.body && document.body.classList.contains('meller-mobile-menu-open');
  };

  const getBlocks = (scope) => {
    const blocks = [];

    if (!scope) return blocks;

    if (scope.matches && scope.matches(BLOCK_SELECTOR)) {
      blocks.push(scope);
    }

    if (scope.querySelectorAll) {
      scope.querySelectorAll(BLOCK_SELECTOR).forEach((block) => {
        blocks.push(block);
      });
    }

    return blocks;
  };

  const addMediaQueryListener = (callback) => {
    if (!mediaQuery) return () => {};

    if (typeof mediaQuery.addEventListener === 'function') {
      mediaQuery.addEventListener('change', callback);
      return () => {
        mediaQuery.removeEventListener('change', callback);
      };
    }

    if (typeof mediaQuery.addListener === 'function') {
      mediaQuery.addListener(callback);
      return () => {
        mediaQuery.removeListener(callback);
      };
    }

    return () => {};
  };

  const addBodyClassListener = (callback) => {
    if (!document.body || !('MutationObserver' in window)) return () => {};

    bodyObserverRegistry.callbacks.add(callback);

    if (!bodyObserverRegistry.observer) {
      bodyObserverRegistry.observer = new MutationObserver(() => {
        bodyObserverRegistry.callbacks.forEach((storedCallback) => {
          storedCallback();
        });
      });

      bodyObserverRegistry.observer.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      });
    }

    return () => {
      bodyObserverRegistry.callbacks.delete(callback);

      if (bodyObserverRegistry.callbacks.size === 0 && bodyObserverRegistry.observer) {
        bodyObserverRegistry.observer.disconnect();
        bodyObserverRegistry.observer = null;
      }
    };
  };

  const setupBlock = (block) => {
    if (!block || registry.has(block)) return;

    const shell = block.querySelector('[data-meller-sticky-shell]');
    const target = block.querySelector('[data-meller-sticky-target]');
    const placeholder = block.querySelector('[data-meller-sticky-placeholder]');

    if (!shell || !target || !placeholder) return;

    const state = {
      sticky: false,
      frame: 0,
      observer: null,
      resizeObserver: null,
      removeMediaQueryListener: null,
      removeBodyClassListener: null,
      onResize: null,
      onScrollFallback: null
    };

    registry.set(block, state);
    block.dataset.mellerStickyInit = 'true';

    const updatePlaceholderHeight = () => {
      if (!state.sticky) return;
      placeholder.style.height = target.offsetHeight + 'px';
    };

    const enableSticky = () => {
      if (state.sticky) {
        updatePlaceholderHeight();
        return;
      }

      state.sticky = true;
      placeholder.style.height = target.offsetHeight + 'px';
      target.classList.add('is-sticky-mobile');
      placeholder.classList.add('is-active');
    };

    const disableSticky = () => {
      if (!state.sticky) return;

      state.sticky = false;
      target.classList.remove('is-sticky-mobile');
      placeholder.classList.remove('is-active');
      placeholder.style.height = '';
    };

    const evaluateStickyState = () => {
      state.frame = 0;

      if (!isMobile() || isMobileMenuOpen()) {
        disableSticky();
        return;
      }

      const rect = shell.getBoundingClientRect();

      if (rect.bottom <= 0) {
        enableSticky();
      } else {
        disableSticky();
      }
    };

    const requestEvaluate = () => {
      if (state.frame) return;

      state.frame = window.requestAnimationFrame(evaluateStickyState);
    };

    if ('IntersectionObserver' in window) {
      state.observer = new IntersectionObserver(
        () => {
          requestEvaluate();
        },
        {
          root: null,
          threshold: [0, 1]
        }
      );

      state.observer.observe(shell);
    } else {
      state.onScrollFallback = requestEvaluate;
      window.addEventListener('scroll', state.onScrollFallback, { passive: true });
    }

    if ('ResizeObserver' in window) {
      state.resizeObserver = new ResizeObserver(() => {
        updatePlaceholderHeight();
        requestEvaluate();
      });

      state.resizeObserver.observe(shell);
      state.resizeObserver.observe(target);
    }

    state.onResize = requestEvaluate;
    window.addEventListener('resize', state.onResize, { passive: true });

    state.removeMediaQueryListener = addMediaQueryListener(requestEvaluate);
    state.removeBodyClassListener = addBodyClassListener(requestEvaluate);

    requestEvaluate();
  };

  const cleanupBlock = (block) => {
    const state = registry.get(block);

    if (!state) return;

    if (state.frame) {
      window.cancelAnimationFrame(state.frame);
    }

    if (state.observer) {
      state.observer.disconnect();
    }

    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
    }

    if (state.onResize) {
      window.removeEventListener('resize', state.onResize);
    }

    if (state.onScrollFallback) {
      window.removeEventListener('scroll', state.onScrollFallback);
    }

    if (state.removeMediaQueryListener) {
      state.removeMediaQueryListener();
    }

    if (state.removeBodyClassListener) {
      state.removeBodyClassListener();
    }

    const target = block.querySelector('[data-meller-sticky-target]');
    const placeholder = block.querySelector('[data-meller-sticky-placeholder]');

    if (target) {
      target.classList.remove('is-sticky-mobile');
    }

    if (placeholder) {
      placeholder.classList.remove('is-active');
      placeholder.style.height = '';
    }

    delete block.dataset.mellerStickyInit;
    registry.delete(block);
  };

  const initStickyATC = (scope) => {
    getBlocks(scope || document).forEach(setupBlock);
  };

  const destroyStickyATC = (scope) => {
    getBlocks(scope || document).forEach(cleanupBlock);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      initStickyATC(document);
    }, { once: true });
  } else {
    initStickyATC(document);
  }

  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', (event) => {
      initStickyATC(event.target || document);
    });

    document.addEventListener('shopify:section:unload', (event) => {
      destroyStickyATC(event.target || document);
    });
  }
})();
